import { AlertTriangle, X } from 'lucide-react'
import { useState } from 'react'

import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

import { usePlatformStatus } from '../hooks/use-platform-status'
import type { PlatformStatusBannerData } from '../lib/platform-status'

/** sessionStorage key holding the `since` of the banner the customer closed. */
const HIDDEN_SINCE_KEY = 'nt:platform-status:hidden-since'

function readHiddenSince(): string | null {
  try {
    return window.sessionStorage.getItem(HIDDEN_SINCE_KEY)
  } catch {
    return null
  }
}

function writeHiddenSince(since: string) {
  try {
    window.sessionStorage.setItem(HIDDEN_SINCE_KEY, since)
  } catch {
    // Storage refused (private mode, policy): the banner still hides until the page reloads.
  }
}

/**
 * The notice itself, without the status read, so the dev preview can render it from a fixture.
 * A polite `status` region, not an `alert`: it appears while the customer works, and must not
 * interrupt a screen reader mid-sentence.
 */
export function PlatformStatusNotice({ banner, onHide }: { banner: PlatformStatusBannerData; onHide: () => void }) {
  return (
    <div className="shrink-0 bg-background px-4 py-3 md:px-6">
      <Alert variant="warning" role="status" data-testid="platform-status-banner">
        <AlertTriangle aria-hidden="true" />
        <AlertTitle>Some actions may not work right now</AlertTitle>
        <AlertDescription>{banner.message}</AlertDescription>
        <AlertAction>
          <Button type="button" variant="ghost" size="icon-sm" aria-label="Hide this notice" onClick={onHide}>
            <X aria-hidden="true" />
          </Button>
        </AlertAction>
      </Alert>
    </div>
  )
}

/**
 * The platform-status banner at the top of every page: in the customer shell's content column
 * (`layouts/customer/CustomerLayout.tsx` via `DashboardShell`'s `banner`, and above the shell-less
 * onboarding wizard), elsewhere at the root (`routes/__root.tsx`). It shows while
 * the API Worker reports an open banner; closing it hides that banner (`since`) for the rest of the
 * browser session, and a new incident shows again.
 */
export function PlatformStatusBanner() {
  const banner = usePlatformStatus()
  const [hiddenSince, setHiddenSince] = useState<string | null>(readHiddenSince)

  if (!banner || banner.since === hiddenSince) return null

  const hide = () => {
    writeHiddenSince(banner.since)
    setHiddenSince(banner.since)
  }

  return <PlatformStatusNotice banner={banner} onHide={hide} />
}
